import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Leaf, ArrowLeft, FileText, Users, CreditCard, AlertTriangle, Scale } from "lucide-react";

export default function CGU() {
  const sections = [
    {
      icon: FileText,
      title: "Article 1 - Objet",
      content: `Les présentes Conditions Générales d'Utilisation (CGU) définissent les modalités d'accès et d'utilisation de la plateforme VF Decide, éditée par VF ACTIONS SAS.

VF Decide est une plateforme d'aide à la décision intégrant l'intelligence artificielle pour accompagner les organisations dans leurs démarches RSE (Responsabilité Sociétale des Entreprises) et leur mise en conformité CSRD (Corporate Sustainability Reporting Directive).

L'accès à la plateforme implique l'acceptation pleine et entière des présentes CGU.`
    },
    {
      icon: Users,
      title: "Article 2 - Inscription et Compte Utilisateur",
      content: `2.1. Pour accéder aux fonctionnalités de VF Decide, l'utilisateur doit créer un compte en fournissant des informations exactes et à jour.

2.2. L'utilisateur s'engage à maintenir la confidentialité de ses identifiants de connexion et à informer immédiatement VF ACTIONS de toute utilisation non autorisée de son compte.

2.3. Un compte est strictement personnel. L'utilisateur ne peut céder ou transférer son compte à un tiers sans autorisation préalable écrite de VF ACTIONS.

2.4. VF ACTIONS se réserve le droit de suspendre ou supprimer tout compte en cas de violation des présentes CGU.`
    },
    {
      icon: CreditCard,
      title: "Article 3 - Services et Abonnements",
      content: `3.1. VF Decide propose plusieurs formules d'abonnement (Starter, Pro, Entreprise) donnant accès à différentes fonctionnalités.

3.2. Les tarifs sont indiqués sur la page Pricing et peuvent être modifiés à tout moment. Toute modification tarifaire sera notifiée aux utilisateurs avec un préavis de 30 jours.

3.3. Les paiements sont effectués via notre prestataire sécurisé Stripe. Aucune donnée de carte bancaire n'est stockée sur nos serveurs.

3.4. L'abonnement est reconduit automatiquement sauf résiliation par l'utilisateur depuis son espace personnel, au moins 48 heures avant la date de renouvellement.

3.5. Conformément à l'article L.221-28 du Code de la consommation, le droit de rétractation ne s'applique pas aux services pleinement exécutés avant la fin du délai de rétractation.`
    },
    {
      icon: Scale,
      title: "Article 4 - Propriété Intellectuelle et Données",
      content: `4.1. L'ensemble de la plateforme VF Decide (code, interface, contenus, algorithmes IA) est protégé par le droit d'auteur et reste la propriété exclusive de VF ACTIONS.

4.2. L'utilisateur conserve la propriété de ses données. Il accorde à VF ACTIONS une licence d'utilisation limitée aux besoins du service (analyse IA, stockage, affichage).

4.3. Les analyses et rapports générés par l'IA sont fournis à titre indicatif et ne constituent pas un conseil juridique, financier ou stratégique engageant la responsabilité de VF ACTIONS.

4.4. L'utilisateur garantit disposer des droits nécessaires sur les données qu'il saisit dans la plateforme.`
    },
    {
      icon: AlertTriangle,
      title: "Article 5 - Responsabilités et Garanties",
      content: `5.1. VF ACTIONS s'engage à assurer la disponibilité de la plateforme avec un taux de disponibilité cible de 99,5% (hors maintenance programmée).

5.2. VF ACTIONS ne saurait être tenue responsable des dommages indirects résultant de l'utilisation ou de l'impossibilité d'utiliser la plateforme.

5.3. L'utilisateur reconnaît que les recommandations de l'IA sont des aides à la décision et non des décisions. La responsabilité finale des choix incombe à l'utilisateur.

5.4. VF ACTIONS applique les standards de sécurité les plus élevés (chiffrement SSL, données hébergées en UE, conformité RGPD) mais ne peut garantir une sécurité absolue contre toute cyberattaque.

5.5. En cas de force majeure (catastrophe naturelle, guerre, grève générale, panne internet majeure), VF ACTIONS est exonérée de toute responsabilité.`
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Leaf className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>VF Decide</span>
          </Link>
          <Link to="/">
            <Button variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <FileText className="w-4 h-4" />
            Conditions Générales
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
            Conditions Générales d'Utilisation
          </h1>
          <p className="text-lg text-muted-foreground">
            En vigueur au 1er janvier 2026 - Version 1.0
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 px-6">
        <div className="max-w-4xl mx-auto space-y-8">
          {sections.map((section, index) => (
            <Card key={index} className="border-0 shadow-md" data-testid={`cgu-section-${index}`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <section.icon className="w-5 h-5 text-primary" />
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground whitespace-pre-line leading-relaxed">
                  {section.content}
                </p>
              </CardContent>
            </Card>
          ))}

          {/* Additional articles summary */}
          <Card className="border-0 shadow-md bg-muted/30">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Articles Complémentaires</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-muted-foreground">
              <p><strong className="text-foreground">Article 6 - Protection des données :</strong> Voir notre Politique de Confidentialité pour les détails sur le traitement RGPD.</p>
              <p><strong className="text-foreground">Article 7 - Modification des CGU :</strong> VF ACTIONS peut modifier les CGU à tout moment avec notification préalable de 30 jours.</p>
              <p><strong className="text-foreground">Article 8 - Droit applicable :</strong> Les présentes CGU sont soumises au droit français. Tout litige sera porté devant les tribunaux compétents de Paris.</p>
              <p><strong className="text-foreground">Article 9 - Contact :</strong> Pour toute question : legal@vf-decide.com</p>
            </CardContent>
          </Card>

          {/* Links */}
          <div className="flex flex-wrap justify-center gap-4 pt-8">
            <Link to="/mentions-legales">
              <Button variant="outline" className="rounded-full">
                Mentions Légales
              </Button>
            </Link>
            <Link to="/confidentialite">
              <Button variant="outline" className="rounded-full">
                Politique de Confidentialité
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-card border-t border-border">
        <div className="max-w-4xl mx-auto text-center text-sm text-muted-foreground">
          <p>© 2026 VF Decide - VF ACTIONS SAS. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
}
